import { useQuery, useSubscription } from "@apollo/client";
import React, { createContext, useContext } from "react";
import { ACTIVE_FLAGS, FLAG_UPDATED } from "./FeatureFlag.gql";
import type {
  TenantActiveFeatureFlagsQueryResponse,
  FeatureFlag,
  FeatureFlagUpdatedSubscriptionResponse,
} from "./FeatureFlags.types";

const FeatureFlagsContext = createContext<Set<string>>(new Set());

export const FeatureFlagsProvider: React.FC<{
  tenantId: string;
  children: React.ReactNode | React.ReactNode[];
}> = ({ tenantId, children }) => {
  const { data, loading, refetch } =
    useQuery<TenantActiveFeatureFlagsQueryResponse>(ACTIVE_FLAGS, {
      variables: { tenantId },
      skip: !tenantId,
    });

  useSubscription<FeatureFlagUpdatedSubscriptionResponse>(FLAG_UPDATED, {
    variables: { tenantId },
    onData: ({ data: subscriptionData }) => {
      // Refetch the active flags when a feature flag is updated
      refetch();
    },
  });

  if (loading) return null; // splash / spinner

  // Handle the response structure with success/error handling
  const featureFlagsResponse = data?.tenantActiveFeatureFlags;

  if (!featureFlagsResponse?.success) {
    console.error("Feature flags error:", featureFlagsResponse?.error);
    // Return empty set on error, or you could provide fallback flags
    return (
      <FeatureFlagsContext.Provider value={new Set()}>
        {children}
      </FeatureFlagsContext.Provider>
    );
  }

  // Extract the feature flag keys from the data array
  const enabledFlags =
    featureFlagsResponse.data?.map((flag: FeatureFlag) => flag.key) ?? [];

  const enabled = new Set<string>(enabledFlags);

  return (
    <FeatureFlagsContext.Provider value={enabled}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

export const useFeatureFlag = (key: string, fallback = false) =>
  useContext(FeatureFlagsContext).has(key) ? true : fallback;
